import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Skeleton,
  Text,
  Flex,
  Badge,
  useColorModeValue,
  useDisclosure,
  Tag,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Card,
  CardBody,
  Stack,
  Divider,
} from "@chakra-ui/react";
import {
  BsPlus,
  BsEye,
  BsPencil,
  BsTrash,
  BsSearch,
  BsThreeDotsVertical,
  BsToggleOff,
  BsToggleOn,
  BsCheckCircle,
  BsXCircle,
} from "react-icons/bs";
import vendorApi from "../services/vendorApi";
import VendorFormModal from "../components/VendorFormModal";

const VendorList = () => {
  const toast = useToast();
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");

  useEffect(() => {
    fetchVendors();
    fetchStats();
  }, [currentPage]);

  useEffect(() => {
    filterVendors();
  }, [vendors, searchTerm, statusFilter]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorApi.getVendors({
        page: currentPage,
        limit: 20,
      });
      // Handle response structure: { success, message, data: { vendors, pagination } }
      const vendorsData = response?.data?.vendors || response?.data || [];
      const pagination = response?.data?.pagination || {};

      console.log("📦 Fetched vendors:", vendorsData);
      setVendors(vendorsData);
      setTotalPages(pagination.pages || 1);
    } catch (error) {
      console.error("❌ Error fetching vendors:", error);
      toast({
        title: "Error fetching vendors",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await vendorApi.getVendorStats();
      setStats(response?.data || { total: 0, active: 0, inactive: 0 });
    } catch (error) {
      console.error("❌ Error fetching vendor stats:", error);
    }
  };

  const filterVendors = () => {
    let filtered = [...vendors];

    if (searchTerm) {
      filtered = filtered.filter(
        (vendor) =>
          vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vendor.gstNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter === "active") {
      filtered = filtered.filter((vendor) => vendor.isActive === true);
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((vendor) => vendor.isActive === false);
    }

    setFilteredVendors(filtered);
  };

  const handleCreateVendor = () => {
    setSelectedVendor(null);
    onOpen();
  };

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    onOpen();
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) {
      return;
    }

    try {
      await vendorApi.deleteVendor(id);
      toast({
        title: "Vendor deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchVendors();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error deleting vendor",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await vendorApi.toggleVendorStatus(id);
      toast({
        title: "Vendor status updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      fetchVendors();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error updating vendor status",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFormSuccess = () => {
    onClose();
    fetchVendors();
    fetchStats();
  };

  if (loading && vendors.length === 0) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="120px" mb={4} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      {/* Header */}
      <Flex
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mb={6}
      >
        <Heading size="lg">Vendor Management</Heading>
        <Button
          leftIcon={<BsPlus />}
          colorScheme="blue"
          onClick={handleCreateVendor}
          w={{ base: "full", md: "auto" }}
        >
          Add Vendor
        </Button>
      </Flex>

      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Total Vendors</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Active Vendors</StatLabel>
              <StatNumber color="green.500">{stats.active}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel>Inactive Vendors</StatLabel>
              <StatNumber color="gray.500">{stats.inactive}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={3}
        mb={6}
        flexWrap="wrap"
      >
        <InputGroup maxW={{ base: "full", md: "300px" }}>
          <InputLeftElement pointerEvents="none">
            <BsSearch />
          </InputLeftElement>
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="All Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          maxW={{ base: "full", md: "180px" }}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </Stack>

      {/* Mobile Cards */}
      <Box display={{ base: "block", md: "none" }}>
        {filteredVendors.length === 0 ? (
          <Box
            bg={bgColor}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={6}
            textAlign="center"
          >
            <Text color="gray.500">No vendors found</Text>
          </Box>
        ) : (
          <SimpleGrid columns={1} spacing={3}>
            {filteredVendors.map((vendor) => (
              <Box
                key={vendor._id || vendor.id}
                bg={bgColor}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                p={4}
              >
                <HStack justify="space-between" align="start" mb={2}>
                  <Box maxW="70%">
                    <Text fontWeight="semibold" noOfLines={2}>
                      {vendor.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={1}>
                      {vendor.email || "N/A"}
                    </Text>
                  </Box>
                  <Badge
                    colorScheme={vendor.isActive ? "green" : "gray"}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    width="fit-content"
                  >
                    {vendor.isActive ? (
                      <>
                        <BsCheckCircle />
                        Active
                      </>
                    ) : (
                      <>
                        <BsXCircle />
                        Inactive
                      </>
                    )}
                  </Badge>
                </HStack>

                <Divider my={2} />

                <SimpleGrid columns={2} spacing={2} mb={3}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      Phone
                    </Text>
                    <Text fontSize="sm">{vendor.phone || "N/A"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      GST Number
                    </Text>
                    <Text fontSize="sm">{vendor.gstNumber || "N/A"}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">
                      City
                    </Text>
                    <Text fontSize="sm">{vendor.address?.city || "N/A"}</Text>
                  </Box>
                </SimpleGrid>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    leftIcon={<BsPencil />}
                    variant="outline"
                    onClick={() => handleEditVendor(vendor)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={
                      vendor.isActive ? <BsToggleOff /> : <BsToggleOn />
                    }
                    variant="outline"
                    onClick={() => handleToggleStatus(vendor._id || vendor.id)}
                  >
                    {vendor.isActive ? "Deactivate" : "Activate"}
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<BsTrash />}
                    colorScheme="red"
                    variant="outline"
                    onClick={() => handleDeleteVendor(vendor._id || vendor.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Table */}
      <Box
        display={{ base: "none", md: "block" }}
        bg={bgColor}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflowX="auto"
      >
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Vendor Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>GST Number</Th>
              <Th>City</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredVendors.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8}>
                  <Text color="gray.500">No vendors found</Text>
                </Td>
              </Tr>
            ) : (
              filteredVendors.map((vendor) => (
                <Tr key={vendor._id || vendor.id}>
                  <Td fontWeight="semibold">{vendor.name}</Td>
                  <Td>{vendor.email || "N/A"}</Td>
                  <Td>{vendor.phone || "N/A"}</Td>
                  <Td>{vendor.gstNumber || "N/A"}</Td>
                  <Td>{vendor.address?.city || "N/A"}</Td>
                  <Td>
                    <Badge
                      colorScheme={vendor.isActive ? "green" : "gray"}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      width="fit-content"
                    >
                      {vendor.isActive ? (
                        <>
                          <BsCheckCircle />
                          Active
                        </>
                      ) : (
                        <>
                          <BsXCircle />
                          Inactive
                        </>
                      )}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        size="sm"
                        icon={<BsPencil />}
                        variant="ghost"
                        onClick={() => handleEditVendor(vendor)}
                        aria-label="Edit vendor"
                      />
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<BsThreeDotsVertical />}
                          size="sm"
                          variant="ghost"
                        />
                        <MenuList>
                          <MenuItem
                            icon={
                              vendor.isActive ? <BsToggleOff /> : <BsToggleOn />
                            }
                            onClick={() =>
                              handleToggleStatus(vendor._id || vendor.id)
                            }
                          >
                            {vendor.isActive ? "Deactivate" : "Activate"}
                          </MenuItem>
                          <MenuItem
                            icon={<BsTrash />}
                            onClick={() =>
                              handleDeleteVendor(vendor._id || vendor.id)
                            }
                            color="red.500"
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Summary and Pagination */}
      <Flex
        justify="space-between"
        align={{ base: "start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 0 }}
        mt={4}
      >
        <Text color="gray.600">
          Showing {filteredVendors.length} of {vendors.length} vendors
        </Text>
        {totalPages > 1 && (
          <HStack w={{ base: "full", md: "auto" }} justify="space-between">
            <Button
              size="sm"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>
            <Text fontSize="sm">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </HStack>
        )}
      </Flex>

      {/* Vendor Form Modal */}
      <VendorFormModal
        isOpen={isOpen}
        onClose={onClose}
        vendor={selectedVendor}
        onSuccess={handleFormSuccess}
      />
    </Box>
  );
};

export default VendorList;
